import React, { useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { MapPin, Store, CheckCircle2, Search, Phone, AlertCircle, Loader2, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";

export default function BranchSelect({ open, onClose, forced = false, goToMenuOnSelect = false }) {
  const { selectedBranch, setSelectedBranch, setPostcode } = useCart();
  const navigate = useNavigate();
  const [pc, setPc] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // { valid, delivering:[], nearby }

  const reset = () => { setPc(""); setResult(null); setBusy(false); };
  const close = () => { onClose(); setTimeout(reset, 250); };

  const check = async (e) => {
    e?.preventDefault();
    if (!pc.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.get(`/branch/for-postcode?postcode=${encodeURIComponent(pc.trim())}`);
      const branches = data.branches || [];
      const delivering = branches.filter((b) => b.delivers).sort((a, b) => (b.is_open ? 1 : 0) - (a.is_open ? 1 : 0));
      let nearby = null;
      if (!delivering.length) {
        const withDist = branches.filter((b) => b.distance != null).sort((a, b) => a.distance - b.distance);
        nearby = withDist[0] || branches[0] || null;
      }
      setResult({ valid: data.valid_postcode, delivering, nearby });
    } catch {
      setResult({ valid: false, delivering: [], nearby: null });
    } finally { setBusy(false); }
  };

  const choose = (b) => {
    setSelectedBranch({ id: b.id, name: b.name });
    if (pc.trim()) setPostcode(pc.trim().toUpperCase()); // remember postcode to prefill checkout
    toast.success(`Branch set: ${b.name}`);
    onClose();
    setTimeout(reset, 250);
    if (goToMenuOnSelect) navigate("/menu");
  };

  const Card = ({ b, tone }) => (
    <button data-testid={`branch-option-${b.id}`} onClick={() => choose(b)}
      className={`w-full text-left rounded-2xl border-2 p-4 transition-colors ${
        selectedBranch?.id === b.id ? "border-metros-red bg-metros-red/5"
          : tone === "green" ? "border-green-200 hover:border-green-500 bg-green-50/40"
          : "border-gray-100 hover:border-metros-red"}`}>
      <div className="flex items-center gap-2 mb-1">
        <MapPin size={18} className="text-metros-red" />
        <h3 className="font-heading font-extrabold text-lg">{b.name}</h3>
        {b.is_open === true && <span data-testid={`branch-open-${b.id}`} className="ml-auto inline-flex items-center gap-1 text-[10px] font-bold text-green-700 bg-green-100 px-2 py-0.5 rounded-full"><span className="w-1.5 h-1.5 rounded-full bg-green-500" /> Open{b.closes_at ? ` · till ${b.closes_at}` : ""}</span>}
        {b.is_open === false && <span data-testid={`branch-closed-${b.id}`} className="ml-auto inline-flex items-center gap-1 text-[10px] font-bold text-red-600 bg-red-100 px-2 py-0.5 rounded-full"><span className="w-1.5 h-1.5 rounded-full bg-red-500" /> Closed{b.opens_at ? ` · opens ${b.opens_at}` : ""}</span>}
      </div>
      {b.address && <p className="text-sm text-muted-foreground">{b.address}</p>}
      {b.phone && <p className="text-sm text-metros-black flex items-center gap-1.5 mt-1"><Phone size={13} className="text-metros-red" /> {b.phone}</p>}
      {b.distance != null && <p className="text-xs text-muted-foreground mt-1">{b.distance} mi away · delivers within {b.delivery_radius_miles} mi</p>}
      <span className="inline-flex items-center gap-1.5 mt-3 text-metros-red font-bold text-sm">
        {tone === "green" ? "Select this branch" : "Select for collection"} <ArrowRight size={15} />
      </span>
    </button>
  );

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !forced) close(); }}>
      <DialogContent className={`max-w-md w-[94vw] rounded-3xl p-6 bg-white border-0 ${forced ? "[&>button]:hidden" : ""}`} data-testid="branch-select-modal">
        <VisuallyHidden><DialogTitle>Find your branch</DialogTitle><DialogDescription>Enter your postcode to find a branch that delivers to you.</DialogDescription></VisuallyHidden>
        <div className="bg-metros-red/10 text-metros-red rounded-2xl p-3 w-fit mx-auto mb-2"><Store size={24} /></div>
        <h2 className="font-heading font-extrabold text-2xl text-center">Find Your Branch</h2>
        <p className="text-sm text-muted-foreground text-center mb-4">
          {forced ? "Enter your postcode to choose a branch and continue to checkout." : "Enter your postcode and we'll check which branch delivers to you."}
        </p>

        <form onSubmit={check} className="flex gap-2 mb-4">
          <input data-testid="branch-select-postcode" autoFocus value={pc} onChange={(e) => setPc(e.target.value.toUpperCase())} placeholder="e.g. KT19 9UR"
            className="flex-1 rounded-xl border border-gray-200 px-4 py-2.5 text-sm uppercase focus:ring-2 focus:ring-metros-red focus:outline-none" />
          <button data-testid="branch-select-check" type="submit" disabled={busy || !pc.trim()} className="bg-metros-black text-white rounded-xl px-4 flex items-center gap-1 disabled:opacity-60">
            {busy ? <Loader2 className="animate-spin" size={16} /> : <Search size={16} />}
          </button>
        </form>

        {result && !result.valid && (
          <div data-testid="branch-result-invalid" className="rounded-2xl p-3 text-sm flex items-start gap-2 bg-red-50 text-metros-red font-semibold">
            <AlertCircle size={18} className="shrink-0 mt-0.5" /> We couldn't find that postcode. Please check it and try again.
          </div>
        )}

        {result && result.valid && result.delivering.length > 0 && (
          <div className="space-y-3">
            <div data-testid="branch-result-success" className="rounded-2xl p-3 text-sm flex items-start gap-2 bg-green-50 text-green-700 font-semibold">
              <CheckCircle2 size={18} className="shrink-0 mt-0.5" /> Great news! We deliver to {pc.trim()}. Choose your branch below.
            </div>
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {result.delivering.map((b) => <Card key={b.id} b={b} tone="green" />)}
            </div>
          </div>
        )}

        {result && result.valid && result.delivering.length === 0 && (
          <div className="space-y-3">
            <div data-testid="branch-result-outside" className="rounded-2xl p-3 text-sm flex items-start gap-2 bg-red-50 text-metros-red font-semibold">
              <AlertCircle size={18} className="shrink-0 mt-0.5" /> Sorry, {pc.trim()} is outside our delivery area. Here's your nearest branch — you can still order for collection.
            </div>
            {result.nearby ? <Card b={result.nearby} tone="red" /> : <p className="text-sm text-center text-muted-foreground py-4">No branches available yet.</p>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
